import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  fetchGoldenLoans,
  fetchGoldenSuiteRun,
  fetchGoldenSuiteRuns,
  runGoldenSuite,
} from "./api";
import GoldenDatasetCuration from "./GoldenDatasetCuration";
import GoldenSuiteDetail from "./GoldenSuiteDetail";
import GoldenSuiteOverview from "./GoldenSuiteOverview";
import GoldenVersionHistory from "./GoldenVersionHistory";
import HumanLabelEditor from "./HumanLabelEditor";
import { getRunLoans, getSuiteRunId } from "./goldenEval";

export default function GoldenDatasetPanel({ env }) {
  const [loans, setLoans] = useState([]);
  const [loansLoading, setLoansLoading] = useState(true);
  const [suiteRuns, setSuiteRuns] = useState([]);
  const [runsLoading, setRunsLoading] = useState(true);
  const [suiteDetail, setSuiteDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedSuiteRunId, setSelectedSuiteRunId] = useState("");
  const [running, setRunning] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const selectedLoan = useMemo(
    () => loans.find((loan) => loan.id === selectedId) || null,
    [loans, selectedId],
  );
  const selectedSuiteLoan = useMemo(
    () =>
      (suiteDetail?.loans || []).find(
        (loan) => loan.goldenLoanId === selectedId,
      ) || null,
    [selectedId, suiteDetail],
  );

  const loadLoans = useCallback(async () => {
    if (env !== "dev") {
      setLoansLoading(false);

      return;
    }
    setLoansLoading(true);
    try {
      const result = await fetchGoldenLoans({ limit: 200 }, env);
      if (!result.success) {
        throw new Error(result.reason || "Failed to load golden loans.");
      }
      const next = Array.isArray(result.loans) ? result.loans : [];
      setLoans(next);
      setSelectedId((current) =>
        current && next.some((loan) => loan.id === current)
          ? current
          : next[0]?.id || null,
      );
    } catch (loadError) {
      setError(loadError.message);
    } finally {
      setLoansLoading(false);
    }
  }, [env]);

  const loadSuiteRuns = useCallback(async () => {
    if (env !== "dev") {
      setRunsLoading(false);

      return;
    }
    setRunsLoading(true);
    try {
      const result = await fetchGoldenSuiteRuns({ limit: 10 }, env);
      if (!result.success) {
        throw new Error(result.reason || "Failed to load suite runs.");
      }
      const summaries = Array.isArray(result.runs) ? result.runs.slice(0, 10) : [];
      const hydrated = await Promise.all(
        summaries.map(async (summary) => {
          if (getRunLoans(summary).length > 0) {
            return summary;
          }
          try {
            const detail = await fetchGoldenSuiteRun(getSuiteRunId(summary), env);
            if (!detail.success) {
              return summary;
            }

            return {
              ...summary,
              ...detail.run,
              loans: Array.isArray(detail.loans) ? detail.loans : [],
            };
          } catch {
            return summary;
          }
        }),
      );
      setSuiteRuns(hydrated);
      setSelectedSuiteRunId((current) =>
        current &&
        hydrated.some((run) => getSuiteRunId(run) === current)
          ? current
          : getSuiteRunId(hydrated[0]),
      );
    } catch (loadError) {
      setError(loadError.message);
    } finally {
      setRunsLoading(false);
    }
  }, [env]);

  const loadSuiteDetail = useCallback(
    async (suiteRunId) => {
      if (!suiteRunId || env !== "dev") {
        setSuiteDetail(null);

        return;
      }
      setDetailLoading(true);
      try {
        const result = await fetchGoldenSuiteRun(suiteRunId, env);
        if (!result.success) {
          throw new Error(result.reason || "Failed to load suite detail.");
        }
        const next = {
          run: { ...(result.run || {}), suiteRunId },
          loans: Array.isArray(result.loans) ? result.loans : [],
        };
        setSuiteDetail(next);
        setSuiteRuns((current) =>
          current.map((run) =>
            getSuiteRunId(run) === suiteRunId
              ? { ...run, ...next.run, loans: next.loans }
              : run,
          ),
        );
      } catch (loadError) {
        toast.error(loadError.message);
      } finally {
        setDetailLoading(false);
      }
    },
    [env],
  );

  useEffect(() => {
    setError(null);
    loadLoans();
    loadSuiteRuns();
  }, [loadLoans, loadSuiteRuns]);

  useEffect(() => {
    loadSuiteDetail(selectedSuiteRunId);
  }, [loadSuiteDetail, selectedSuiteRunId]);

  useEffect(() => {
    const status = String(suiteDetail?.run?.status || "").toLowerCase();
    if (!["queued", "running"].includes(status) || !selectedSuiteRunId) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      loadSuiteDetail(selectedSuiteRunId);
    }, 10000);

    return () => window.clearInterval(intervalId);
  }, [loadSuiteDetail, selectedSuiteRunId, suiteDetail?.run?.status]);

  const toggleSelected = (goldenLoanId) => {
    setSelectedIds((current) =>
      current.includes(goldenLoanId)
        ? current.filter((id) => id !== goldenLoanId)
        : [...current, goldenLoanId],
    );
  };

  const handleSuiteQueued = useCallback((result) => {
    if (!result?.suiteRunId) {
      return;
    }
    const queuedCount =
      Number(result.queuedCount) || (result.runAfterClone ? 1 : 0);
    const failedCount = Number(result.failedCount) || 0;
    const optimisticRun = {
      suiteRunId: result.suiteRunId,
      status: "queued",
      createdAt: Date.now(),
      loanCount: Number(result.totalCount) || queuedCount + failedCount,
      completedCount: 0,
      failedCount,
      queuedCount,
      loans: [],
    };
    setSuiteRuns((current) => [
      optimisticRun,
      ...current.filter(
        (run) => getSuiteRunId(run) !== result.suiteRunId,
      ),
    ].slice(0, 10));
    setSelectedSuiteRunId(result.suiteRunId);
  }, []);

  const runSuite = async (goldenLoanIds) => {
    if (env !== "dev") {
      toast.error("Golden suite runs are only available in Dev.");

      return;
    }
    setRunning(true);
    try {
      const result = await runGoldenSuite(
        goldenLoanIds?.length ? { goldenLoanIds } : {},
        env,
      );
      if (!result.success && !result.queuedCount) {
        throw new Error(result.reason || "Could not queue the golden suite.");
      }
      handleSuiteQueued(result);
      if (result.queuedCount > 0) {
        toast.success(
          `Queued ${result.queuedCount} golden loan${
            result.queuedCount === 1 ? "" : "s"
          } in suite ${result.suiteRunId}.`,
        );
      }
      if (result.failedCount > 0) {
        toast.error(`${result.failedCount} loan(s) failed to queue.`);
      }
    } catch (runError) {
      toast.error(runError.message);
    } finally {
      setRunning(false);
    }
  };

  const addLoan = (loan) => {
    if (!loan) {
      loadLoans();

      return;
    }
    setLoans((current) => [
      loan,
      ...current.filter((item) => item.id !== loan.id),
    ]);
    setSelectedId(loan.id);
  };

  if (env !== "dev") {
    return (
      <div className="icr-golden">
        <div className="icr-dev-only">
          <span>Dev only</span>
          <h2>Golden evaluation platform</h2>
          <p>
            Switch the environment to Dev to curate golden loans, run suites,
            edit optional human labels, and review Addy against Harness.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="icr-golden">
      <header className="icr-golden-head">
        <div>
          <h2>Golden dataset</h2>
          <p>Curate loans, run them, and compare Addy against the Harness.</p>
        </div>
        <div className="icr-golden-head__actions">
          <span className="icr-dev-badge">Dev only</span>
          <button
            type="button"
            className="icr-btn"
            onClick={() => setShowHelp((value) => !value)}
          >
            {showHelp ? "Hide guide" : "How it works"}
          </button>
        </div>
      </header>

      {showHelp && (
        <ol className="icr-golden-guide">
          <li>
            <strong>Pick loans.</strong> Add loans to the golden set in Step 1.
          </li>
          <li>
            <strong>Run.</strong> “Run all” calculates each loan two ways — Addy
            and the Harness — in the background.
          </li>
          <li>
            <strong>Review.</strong> Step 2 shows each run and where the two
            agree, differ, and why.
          </li>
        </ol>
      )}

      {error && <div className="icr-error">{error}</div>}

      <section className="icr-step">
        <div className="icr-step__label">
          <span>Step 1</span>
          <h3>Your golden loans</h3>
          <p>Add loans, then run the whole set or just the selected ones.</p>
        </div>
        <GoldenDatasetCuration
          env={env}
          loans={loans}
          loading={loansLoading}
          selectedId={selectedId}
          selectedIds={selectedIds}
          running={running}
          onSelect={setSelectedId}
          onToggleSelected={toggleSelected}
          onAdded={addLoan}
          onRemoved={(goldenLoanId) => {
            setLoans((current) =>
              current.filter((loan) => loan.id !== goldenLoanId),
            );
            setSelectedIds((current) =>
              current.filter((id) => id !== goldenLoanId),
            );
            if (selectedId === goldenLoanId) {
              setSelectedId(null);
            }
          }}
          onRunAll={() => runSuite()}
          onRunSelected={() => runSuite(selectedIds)}
          onSuiteQueued={handleSuiteQueued}
          onRefresh={loadLoans}
        />
      </section>

      <section className="icr-step">
        <div className="icr-step__label">
          <span>Step 2</span>
          <h3>Results</h3>
          <p>Pick a run to see how each loan did and why amounts differ.</p>
        </div>
        <GoldenSuiteOverview
          runs={suiteRuns}
          loading={runsLoading}
          selectedSuiteRunId={selectedSuiteRunId}
          onSelectRun={setSelectedSuiteRunId}
          onRefresh={loadSuiteRuns}
        />

        <GoldenSuiteDetail
          env={env}
          run={suiteDetail?.run}
          loans={suiteDetail?.loans || []}
          loading={detailLoading}
          recentRuns={suiteRuns}
          onRefresh={() => loadSuiteDetail(selectedSuiteRunId)}
        />
      </section>

      {selectedLoan && (
        <section className="icr-step">
          <button
            type="button"
            className="icr-advanced-toggle"
            aria-expanded={showAdvanced}
            onClick={() => setShowAdvanced((value) => !value)}
          >
            {showAdvanced ? "− Hide" : "+ Show"} advanced tools
            <small>Optional: human-approved answer and version history</small>
          </button>

          {showAdvanced && (
            <>
              <HumanLabelEditor
                env={env}
                goldenLoan={selectedLoan}
                suiteLoan={selectedSuiteLoan}
                suiteRunId={selectedSuiteRunId}
                onSaved={(humanLabel) =>
                  setLoans((current) =>
                    current.map((loan) =>
                      loan.id === selectedLoan.id
                        ? { ...loan, humanLabel }
                        : loan,
                    ),
                  )
                }
                onSuiteQueued={handleSuiteQueued}
              />
              <GoldenVersionHistory goldenLoan={selectedLoan} env={env} />
            </>
          )}
        </section>
      )}
    </div>
  );
}
